import {Popover2, Tooltip2} from '@api'
import {Tooltip} from '@material-ui/core'
import {BuilderCardLoader, GroupByGroupStoreContext} from '@modules/catalog'
import {useVipLocalStore, VipLocalStore} from '@modules'
import {DragDropEventHandlers, ICatalogFieldConfig, LibraryItemConfig, onSiteError, StoresInstance} from '@stores'
import {DragEventPlaceholders} from 'centric-ui-components'
import {autorun, runInAction, trace} from 'mobx'
import {useContext} from 'react'
import {GroupByGroupStore, HeroSwatchesContextMenu, IntentionallyLeftBlank, useGroupByGroupStore} from './'
import {_, React, observer, useLocalStore, observable, action, computed, useHover, useEffect, pluralize} from '@api'
import css from './GroupByGroupHeroCards.css'
import {IContextMenuItems} from '@design-board'
import {Popover} from '@blueprintjs/core'

export type ISubgroupHoverItem = { item: any, field?: ICatalogFieldConfig, subgroup: GroupByGroupHeroCardsStore }

export class GroupByGroupHeroCardsStore extends VipLocalStore implements IContextMenuItems {
  constructor(stores: StoresInstance, public groupStore: GroupByGroupStore) {
    super(stores)
    groupStore.subgroupStore = this
  }

  get contextMenuItems() {
    return <GroupByGroupHeroCardsStoreContext.Provider value={this}>
      <GroupByGroupStoreContext.Provider value={this.groupStore}>
        <HeroSwatchesContextMenu/>
      </GroupByGroupStoreContext.Provider>
    </GroupByGroupHeroCardsStoreContext.Provider>
  }

  @computed get customSort() {
    return this.subGroupDefinition?.customSort
  }

  @computed get subGroupDefinition() {
    return this.groupStore.grouping?.fieldDefinition?.group?.subGroup ?? {field: this.subGroup}
  }

  @computed get subGroup() {
    const {groupStore: {grouping}} = this
    return grouping.settingsForLevel.heroCardField ?? grouping?.fieldDefinition?.group?.subGroup?.field
  }

  @computed get field() {
    const {catalog: {_fieldByName}, subGroup} = this
    return subGroup ? _fieldByName[subGroup] : null
  }

  @computed get esField() {
    const {catalog: {esFieldFor, _fieldByName}, field} = this
    return field ? esFieldFor(field, true) : null
  }

  @computed get columns() {
    const {subGroupDefinition} = this,
      items = this.items ?? []
    if (subGroupDefinition?.columns) {
      const {columns} = subGroupDefinition
      if (_.isNumber(columns)) return columns
      if (_.isFunction(columns)) return columns(items)
    }

    return Math.max(
      2,
      items?.length > 7 ? 4
                        : items.length > 3 ? 3 : items.length - 1 // due to the hero image
    )
  }

  @computed get searchBody() {
    const {groupStore: {childGroupStore: {productQueryVariables}}, esField, subGroup} = this

    return esField ? {
      ...productQueryVariables,
      size: 0,
      aggs: {
        subgroup: {
          terms: {
            field: esField,
            size: 100
          },
          //These results are incorrect in rh poc - get them direct from the underlying index :(
          aggs: {
            item: {
              top_hits: {
                size: 1,
                _source: `${subGroup}.id`
              }
            }
          }
        }
      }
    } : null
  }

  get items() { return this.field ? this.groupStore.subgroupItems.get(this.field.field) : null}

  set items(v) {
    this.groupStore.subgroupItems.set(this.field.field, v)
  }

  registerEffects = () => {
    const {groupStore, catalog, field, groupBy} = this
    if (!field || catalog.isShowingAssortment) return

    let first = true
    const runQueryName = `Run subgroup query for ${groupStore.groupLabel} -${catalog.labelForField(field)}`

    const disposers = [
      autorun(async () => {
        if (groupStore.grouping && (!groupBy.settings.heroCards.hideCollapsed || groupStore.isExpanded) && this.field) {
          const {searchBody} = this, {refetchId} = catalog

          await runInAction(runQueryName, async () => {
            if (first && !_.isEmpty(this.items)) {
              first = false
            } else {
              this.items = await this.runQuery()
            }
          })
        }
      }, {name: runQueryName})
    ]

    return () => {
      disposers.forEach(d => d())
    }
  }

  @action runQuery = async () => {
    const {es, catalog, searchBody, subGroup, customSort, field, subGroupDefinition, groupStore: store} = this

    if (!searchBody) return

    this.loading = true

    try {
      const {aggregations: {subgroup: {buckets}}} = await es.search({
        meta: `Custom subgroup query for ${subGroup}`,
        index: catalog.index,
        body: searchBody
      }, {log: {query: false}})

      // Hack to query across to underlying index to get full attributes as they are missing on RH POC
      //DEBUG && console.log(`Hack to query across to underlying index to get full attributes as they are missing on RH POC`)
      let items = _.flatMap(buckets, b => _.get(_.first<any>(b.item.hits.hits)?._source, subGroup))
      const ids = items.map(i => i.id)

      items = es.unwrapHitsResult(await es.search({
        index: field.esIndex,
        meta: `Hack to query across to underlying index to get full attributes as they are missing on RH POC ${JSON.stringify(ids)}`,
        body: {
          size: ids.length,
          query: {terms: {id: ids}}
        }
      }, {})).items

      if (subGroupDefinition?.filter) {
        items = subGroupDefinition.filter(items)
      }
      if (customSort) {
        try {
          items = customSort(items, {store, stores: this.stores})
        } catch (err) {
          onSiteError(err)
        }
      }
      return items
    } finally {
      this.loading = false
    }
  }

  @observable loading = true

  get hoverItem() { return this.groupStore.contextualHoverItem as ISubgroupHoverItem }

  set hoverItem(item: ISubgroupHoverItem) { this.groupStore.contextualHoverItem = item ? {...item, field: item.subgroup.field} : null }

  onMouseLeave = e => {
    this.hoverItem = null
  }

  @computed get library(): LibraryItemConfig {
    const {subGroup, catalog} = this,
      field = catalog._fieldByName[subGroup]

    if (field?.esIndex) {
      return catalog.libraryByIndex[field?.esIndex]
    }

    return null
  }

  @computed get associatedWorkspace() {
    const {library, catalog} = this
    return library ? catalog.workspaceByIndex[library.index] : null
  }

  dragPlaceholderFor = _.memoize((item: ISubgroupHoverItem) => {
    const {field, dragDrop, catalog} = this
    return dragDrop.handlersFor({target: field.esIndex, id: catalog.idFor(item)})
  })

  mouseEventsFor: (item: ISubgroupHoverItem) => Partial<React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>> = _.memoize(item => ({
    onMouseEnter: e => {
      this.hoverItem = {item, subgroup: this}
    },
    onMouseLeave: e => {
      if (this.hoverItem?.item === item) {
        this.hoverItem = null
      }
    },
    onClick: e => {
      e.stopPropagation() // Otherwise click on group will trigger and clear our selection
      this.groupStore.selectContextualHoverCatalogItems(e)
    },
    onDoubleClick: e => {
      const {library, catalog} = this
      if (library) {
        e.stopPropagation()
        catalog.sidebar.left.navigateToLibrary({libraryId: library.id, where: [{value: catalog.idFor(item)}]}, true)
      }
    }
  }))

  @observable backgroundRgb: Array<number>

  @computed get style() {
    const {backgroundRgb} = this

    if (backgroundRgb) {
      const [r, g, b] = backgroundRgb
      return {background: `rgb(${r}, ${g}, ${b})`}
    }

    return {}
  }

  onImageLoad = (e: React.UIEvent<HTMLImageElement>) => {
    const image = e.currentTarget
    if (image) {
      // image.crossOrigin = 'Anonymous'
      try {
        // this.backgroundRgb = backgroundColorThief.getBackGroundColor(image)
        // const palette = backgroundColorThief.getPalette(image)
        //
        // console.log({palette, bg: this.backgroundRgb})
      } catch (err) {

      }
    }
  }

  @action onContextMenu = (e) => {
    this.contextMenu.onComponentContextMenu(e, this)
  }
}

export const GroupByGroupHeroCardsStoreContext = React.createContext<GroupByGroupHeroCardsStore>(undefined)
export const useGroupByGroupHeroCardsStore = () => useContext(GroupByGroupHeroCardsStoreContext)

export const GroupByGroupHeroCards = observer(function GroupByGroupHeroCards() {
  const groupStore = useGroupByGroupStore(),
    {grouping: {hasSubGroup}, catalog} = groupStore

  const store = useVipLocalStore((ws) => new GroupByGroupHeroCardsStore(ws, groupStore)),
    {catalog: {isShowingAssortment, groupBy: {settings: {heroCards: {show}}}}, onContextMenu, field, associatedWorkspace, items, loading, columns, groupStore: {selectedCatalogItemsInGroup}} = store

  useEffect(() => store.registerEffects(), [])
  //const [hoverRef, isHovering] = useHover()

  if (isShowingAssortment || !hasSubGroup || !field || !show) { return null}

  const content = (
    <div
      className={css.container}
      data-group={field.field}
      data-loading={loading}
      onMouseLeave={store.onMouseLeave}
      style={{'--object-fit': catalog.objectFitFor(associatedWorkspace)} as any}
    >
      {_.isEmpty(items) && !loading &&
	   <Tooltip2 content={`No ${pluralize(catalog.labelForField(field))}`}>
		   <IntentionallyLeftBlank className={css.intentionallyLeftBlank} showText/>
	   </Tooltip2>}

      {(items ?? []).map((item, i) => <SubgroupItem item={item} index={i} key={i}/>)}
    </div>)

  return (
    <GroupByGroupHeroCardsStoreContext.Provider value={store}>
      {false && <Popover2
		  interactionKind='hover'
		  placement='right-start'
		  minimal
		  hoverOpenDelay={400}
		  popoverClassName={css.popover}
		  disabled={true || /* Turning off in lieue of click to open left sidebar */
                    _.isEmpty(catalog.visibleCardFieldsFor(field, null))}
		  content={<PopoverContent/>}
		  modifiers={{
            offset: {
              // offset: '10, 0'
            }
          }}
		  className={css.root}
	  >
        {content}
	  </Popover2>}

      <div
        className={css.root}
        data-type='GroupByGroupHeroCards'
        data-expanded={groupStore.isExpanded}
        data-contextual-hover-item={groupStore.contextualHoverCatalogItem != null || !_.isEmpty(selectedCatalogItemsInGroup)}
        onContextMenu={onContextMenu}
        style={{
          '--columns': columns
        } as any}
      >
        {content}
      </div>
    </GroupByGroupHeroCardsStoreContext.Provider>
  )
})

const PopoverContent = observer(function PopoverContent() {
  const {hoverItem, field, catalog} = useGroupByGroupHeroCardsStore()
  return hoverItem ? <BuilderCardLoader hideButtons isPopover levelOfDetail={1} nested={{field, data: hoverItem}} field={field} id={catalog.idFor(hoverItem)}/> : null
})

const SubgroupItem = observer<{ item: any, index: number }>(function SubgroupItem({item, index}) {
  const {catalog, style, mouseEventsFor, groupStore: {matchesContextualCatalogItem}, field, dragPlaceholderFor} = useGroupByGroupHeroCardsStore(),
    id = catalog.idFor(item), dragDropEventHandlers = dragPlaceholderFor(item)

  return (
    <div className={css.item}
         style={style}
         data-id={id}
         data-index={index}
         data-matches-group-hover-item={matchesContextualCatalogItem(item, field.field)}
         {...mouseEventsFor(item)}
         {...dragDropEventHandlers.events}
    >
      <DragEventPlaceholders id={id} dropType={field.esIndex}/>
      <img src={catalog.imageUrlFor(_.first(item.images))}
        // onLoad={onImageLoad}
        //    crossOrigin="anonymous"
      />

    </div>)
})


// WEBPACK FOOTER //
// ./src/modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_Groups/GroupByCatalog_LoadMore/GroupByGroupHeroCards.tsx